// ---- Terrain flatten zones ----
// Some things need level ground under them — a building's footprint, a plaza, a
// spawn clearing. Rather than special-casing each in heightAt(), they're listed
// here as discs: perfectly flat to `y` within `r`, then eased back to the natural
// terrain over `feather`. heightAt() applies them as its final step, so the
// terrain mesh, deco placement, minimap and player all agree on the same ground.
//
// This module imports nothing (keep it cycle-free — terrain.ts depends on it).
// When you place a walkable structure that needs flat ground (see
// structure/index.ts), add a zone here covering its footprint.

export type FlatZone = {
  x: number;
  z: number;
  /** Radius that is fully flattened to `y`. */
  r: number;
  /** Distance over which it blends back to natural terrain beyond `r`. */
  feather: number;
  /** Height to flatten to. */
  y: number;
};

// Ad-hoc zones not tied to a placed structure (a pasture, a camp clearing).
// These are CONTENT since plan 20: the active map doc's `terrain.flats`
// (content/maps/<id>.json), set here by src/content/load.ts at hydration —
// mirroring setStructureFlatZones below. Structure pads are NOT listed there;
// buildWorld derives one flatten zone per structure placement and feeds
// them in via setStructureFlatZones(), so adding a building is a single
// placement entry (see structure/index.ts) with no hand-synced disc to maintain.
let baseZones: FlatZone[] = [];

/** Replace the map-authored flatten zones (called by content load / the studio
 *  world editor). */
export function setBaseFlatZones(zones: FlatZone[]): void {
  baseZones = zones;
}

// Flatten pads for placed structures, replaced each time the world is (re)built.
let structureZones: FlatZone[] = [];

/** Replace the structure-derived flatten pads (called by buildWorld). */
export function setStructureFlatZones(zones: FlatZone[]): void {
  structureZones = zones;
}

/** The CURRENT structure pads — the server captures a copy per map right after
 *  each headless build (plan 21: makeTerrainCtx composes them with the doc's
 *  own flats, so only the pads are needed here). */
export function currentStructureFlatZones(): FlatZone[] {
  return [...structureZones];
}

/** Blend a natural terrain height toward any flatten zone covering (x,z). */
export function applyFlatten(x: number, z: number, h: number): number {
  for (const f of baseZones) h = blendZone(x, z, h, f);
  for (const f of structureZones) h = blendZone(x, z, h, f);
  return h;
}

/** Pure applyFlatten over an EXPLICIT zone list (plan 21: the server's per-map
 *  TerrainCtx captures each map's base + structure-pad zones and flattens
 *  against them, independent of the module state above). */
export function applyFlattenTo(x: number, z: number, h: number, zones: FlatZone[]): number {
  for (const f of zones) h = blendZone(x, z, h, f);
  return h;
}

function blendZone(x: number, z: number, h: number, f: FlatZone): number {
  const d = Math.hypot(x - f.x, z - f.z);
  if (d >= f.r + f.feather) return h;
  // t: 0 inside the flat core → 1 at the outer edge of the feather.
  const t = d <= f.r ? 0 : (d - f.r) / f.feather;
  const k = t * t * (3 - 2 * t); // smoothstep
  return f.y + (h - f.y) * k;
}
